/**
 * Unified Execution Types
 *
 * Types for the unified execution system supporting dual-mode providers
 * and multiple execution modes.
 */

// ==================== Provider Types ====================

/**
 * Provider selection mode for LLM calls
 */
export const AgentProviderMode = {
  LOCAL: 'local',
  API: 'api',
  AUTO: 'auto',
  HYBRID: 'hybrid',
} as const

export type AgentProviderMode = (typeof AgentProviderMode)[keyof typeof AgentProviderMode]

/**
 * Execution pipeline mode
 */
export const AgentExecutionMode = {
  DIRECT: 'direct',
  SIMPLE: 'simple',
  TRIAD: 'triad',
  FULL: 'full',
} as const

export type AgentExecutionMode = (typeof AgentExecutionMode)[keyof typeof AgentExecutionMode]

/**
 * Provider status response
 */
export interface ProviderStatus {
  local: boolean
  local_model?: string
  local_models_available?: string[]
  ollama_models?: string[]
  anthropic: boolean
  openai: boolean
  google: boolean
  default_provider?: string
  preferred_provider?: string
  fallback_chain?: string[]
}

// ==================== Agent Types ====================

/**
 * Unified agent with all provider and execution settings
 * Properties are optional to support both full and summary API responses
 */
export interface UnifiedAgent {
  id: string
  name: string
  description?: string
  workspace_id?: string
  framework?: 'langchain' | 'crewai' | 'adk' | 'custom'
  status?: 'draft' | 'active' | 'archived'

  // Classification
  team?: string
  specialist_type?: string
  is_ziro_specialist?: boolean
  is_template?: boolean
  is_custom?: boolean
  is_system_agent?: boolean

  // Provider Configuration
  provider_mode?: AgentProviderMode | string
  llm_provider?: string
  local_model?: string
  local_model_coding?: string
  local_temperature?: number
  api_provider?: string
  api_model?: string
  api_temperature?: number

  // Execution Configuration
  execution_mode?: AgentExecutionMode | string
  enable_quality_review?: boolean
  quality_threshold?: number
  max_revisions?: number
  complexity_threshold?: number
  auto_scale_workers?: boolean
  max_workers?: number

  // Orchestration
  parent_agent_id?: string
  can_delegate?: boolean
  can_be_orchestrated?: boolean
  role?: string

  // Behavior
  system_prompt?: string
  tools?: string[]
  configuration?: Record<string, any>

  created_at?: string
  updated_at?: string
}

/**
 * Simplified agent for listing/selection
 */
export interface UnifiedAgentSummary {
  id: string
  name: string
  description?: string
  team?: string
  specialist_type?: string
  is_ziro_specialist: boolean
  is_template: boolean
  provider_mode: AgentProviderMode
  execution_mode: AgentExecutionMode
  status: 'draft' | 'active' | 'archived'
}

// ==================== Execution Types ====================

/**
 * Runtime execution options that override agent defaults
 */
export interface ExecutionOptions {
  // Provider overrides
  provider_mode?: AgentProviderMode
  local_model?: string
  api_provider?: string
  api_model?: string
  temperature?: number

  // Execution overrides
  execution_mode?: AgentExecutionMode
  enable_quality_review?: boolean
  quality_threshold?: number
  max_revisions?: number

  // Parallel execution
  enable_parallel?: boolean
  max_concurrent?: number
}

/**
 * Request for unified execution
 */
export interface UnifiedExecuteRequest {
  // Task definition
  prompt: string

  // Agent selection (one required)
  agent_id?: string
  agent_ids?: string[]
  team_id?: string

  // Orchestration mode
  use_ai_orchestration?: boolean
  auto_detect_specialists?: boolean

  // Execution options
  options?: ExecutionOptions

  // Context
  workspace_id?: string
  context?: Record<string, any>
}

/**
 * Result from a single task/specialist execution
 */
export interface TaskResult {
  task_id: string
  specialist: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'revision'
  result?: string
  quality_score?: number
  revision_count: number
  provider_used: string
  model_used: string
  duration_ms?: number
  token_usage?: {
    input: number
    output: number
  }
  cost?: number
}

/**
 * Execution status
 */
export type UnifiedExecutionStatus =
  | 'queued'
  | 'analyzing'
  | 'executing'
  | 'reviewing'
  | 'revising'
  | 'aggregating'
  | 'completed'
  | 'failed'
  | 'cancelled'

/**
 * Task result item for array-style task_results
 */
export interface TaskResultItem {
  task_id?: string
  specialist?: string
  specialist_type?: string
  status: string  // Flexible to handle various API response formats
  result?: string
  output?: string
  quality_score?: number
  revision_count?: number
  provider_used?: string
  model_used?: string
  duration_ms?: number
  token_usage?: {
    input: number
    output: number
  }
  cost?: number
}

/**
 * Response from unified execution
 * Flexible to support different API response formats
 */
export interface UnifiedExecuteResponse {
  execution_id: string
  status: UnifiedExecutionStatus | string

  // Output - different API versions may use different names
  final_output?: string
  output?: string

  // Task results - can be array or record depending on API version
  task_results?: TaskResultItem[] | Record<string, TaskResult>

  // Plan info
  plan?: {
    tasks: number
    parallel_groups?: string[][]
    complexity: number
  }

  // Quality metrics - different API versions
  overall_quality_score?: number
  quality_score?: number

  // Revision tracking
  total_revisions?: number
  revision_count?: number

  // Provider info
  provider_stats?: Record<string, number>
  provider_used?: string

  // Token usage
  total_duration_ms?: number
  total_tokens?: number
  tokens_used?: number
  total_cost?: number

  // Timestamps
  created_at?: string
  started_at?: string
  completed_at?: string

  // Additional metadata
  metadata?: Record<string, any>
}

// ==================== Specialist Types ====================

/**
 * Specialist type info
 */
export interface SpecialistType {
  type: string
  description: string
  agents: Array<{
    id: string
    name: string
    provider_mode: string
    execution_mode: string
  }>
}

/**
 * Analysis result for prompt
 */
export interface PromptAnalysis {
  prompt: string
  analysis: {
    tasks: Array<{
      id: string
      description: string
      specialist_type: string
      complexity: number
      dependencies: string[]
    }>
    parallel_groups: string[][]
    total_complexity: number
    estimated_cost: number
  }
  available_specialists: Array<{
    type: string
    agent_id: string
    agent_name: string
    provider_mode: string
  }>
  recommended_execution_mode: AgentExecutionMode
}

// ==================== API Client Methods ====================

/**
 * Unified API client interface
 */
export interface UnifiedAPIClient {
  // Execution
  executeUnified(request: UnifiedExecuteRequest): Promise<UnifiedExecuteResponse>
  analyzePrompt(prompt: string): Promise<PromptAnalysis>

  // Providers
  getProviderStatus(): Promise<ProviderStatus>

  // Agents
  getUnifiedAgents(params?: {
    team?: string
    specialist_type?: string
    is_ziro_specialist?: boolean
    provider_mode?: AgentProviderMode
    include_inactive?: boolean
  }): Promise<UnifiedAgentSummary[]>
  getUnifiedAgent(agentId: string): Promise<UnifiedAgent>

  // Specialists
  listSpecialists(): Promise<SpecialistType[]>
}
